// Groww-style stock list: company, intraday sparkline, price and change, strength and signals.
import { useRouter } from '@/lib/nav';
import { NSE, NSE_NAMES, zoneState, type Analysed } from '@planb/core';
import { liveSweep } from '@/charts/board';
import { cn } from '@/lib/utils';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Change, Initials, Sparkline, StrengthBar, DeskIcon } from './market-ui';

const fmtPx = NSE.formatPrice;

export function MoversTable({ rows, t, href, compact, current, empty = 'No stocks match right now.', onAsk }: {
  rows: Analysed[]; t: number; href: (sym: string) => string; compact?: boolean; current?: string; empty?: string;
  /** An Ask button on each row: opens the desk with a question about that stock. */
  onAsk?: (s: Analysed) => void;
}) {
  const router = useRouter();
  if (!rows.length) return <p className="px-5 py-10 text-center text-sm text-muted-foreground">{empty}</p>;
  return (
    <Table>
      <TableHeader>
        <TableRow className="hover:bg-transparent">
          <TableHead className="pl-5">Company</TableHead>
          <TableHead className="hidden sm:table-cell">Today</TableHead>
          <TableHead className="text-right">Price</TableHead>
          <TableHead className="hidden md:table-cell">Strength</TableHead>
          {!compact && <TableHead className="hidden text-right lg:table-cell">Volume</TableHead>}
          {!compact && <TableHead className="hidden text-right lg:table-cell">Flow (est.)</TableHead>}
          <TableHead className={cn('pr-5', compact ? 'hidden xl:table-cell' : 'hidden md:table-cell')}>Signals</TableHead>
          {onAsk && <TableHead className="w-10 pr-3"><span className="sr-only">Ask the desk</span></TableHead>}
        </TableRow>
      </TableHeader>
      <TableBody>
        {rows.map((s) => {
          const sw = liveSweep(s, t);
          const chips = (
            <div className="flex flex-wrap gap-1">
              {sw && <Signal className="bg-warn-soft text-warn-ink">Sweep {sw.key}{sw.type === 'bear' ? '↓' : '↑'}</Signal>}
              {s.eventRisk && <Signal className="bg-warn-soft text-warn-ink">Event risk</Signal>}
              {!s.eventRisk && (() => {
                const z = [...s.obs].reverse().find((o) => !o.mitigated && o.type === (s.bull ? 'bull' : 'bear'));
                if (!z) return null;
                const st = zoneState(z);
                return <Signal className="bg-brand-soft text-brand-strong">{st === 'fresh' ? 'Fresh zone' : st === 'testing' ? 'Zone · testing' : st === 'reacted' ? 'Zone · reacted' : 'Zone · no reaction'}</Signal>;
              })()}
              {s.hasOI && <Signal className="bg-muted text-muted-foreground">{s.buildUp}</Signal>}
              <Signal className={s.withSector ? 'bg-muted text-muted-foreground' : 'bg-transparent text-muted-foreground ring-1 ring-border'}>{s.withSector ? 'With sector' : 'Alone'}</Signal>
            </div>
          );
          return (
            <TableRow key={s.symbol} data-sym={s.symbol} onClick={(e) => {
              const url = href(s.symbol);
              if (e.metaKey || e.ctrlKey) window.open(url, '_blank'); else router.push(url);
            }} className={cn('cursor-pointer hover:bg-tint', s.symbol === current && 'bg-brand-soft hover:bg-brand-soft')}>
              <TableCell className="py-3 pl-5">
                <div className="flex min-w-0 items-center gap-3">
                  <Initials symbol={s.symbol} size="sm" />
                  <div className="min-w-0">
                    <a href={href(s.symbol)} onClick={(e) => e.preventDefault()} className="block max-w-56 truncate font-medium text-ink">{NSE_NAMES[s.symbol] ?? s.symbol}</a>
                    <div className="text-xs text-muted-foreground">{s.symbol} · {s.sector}</div>
                    <div className={cn('mt-1.5', compact ? 'xl:hidden' : 'md:hidden')}>{chips}</div>
                  </div>
                </div>
              </TableCell>
              <TableCell className="hidden sm:table-cell"><Sparkline series={s.series} up={s.bull} /></TableCell>
              <TableCell className="text-right">
                <div className="num text-[13.5px] text-ink">{NSE.currencySymbol}{fmtPx(s.ltp)}</div>
                <Change pct={s.pct} className="text-xs" />
              </TableCell>
              <TableCell className="hidden md:table-cell"><StrengthBar score={s.score} bull={s.bull} /></TableCell>
              {!compact && <TableCell className="num hidden text-right text-[13px] lg:table-cell">{s.relVol.toFixed(2)}×</TableCell>}
              {!compact && (
                <TableCell className="hidden text-right lg:table-cell">
                  {s.flow ? <Change pct={s.flow.deltaPct} className="text-[13px]" /> : <span className="text-muted-foreground">—</span>}
                </TableCell>
              )}
              <TableCell className={cn('pr-5', compact ? 'hidden xl:table-cell' : 'hidden md:table-cell')}>
                {chips}
              </TableCell>
              {onAsk && (
                <TableCell className="pr-3">
                  <button type="button" title={`Ask the desk about ${s.symbol}`} aria-label={`Ask the desk about ${s.symbol}`}
                    className="grid size-7 place-items-center rounded-full text-brand-strong hover:bg-brand-soft"
                    onClick={(e) => { e.stopPropagation(); onAsk(s); }}><DeskIcon className="size-3.5" /></button>
                </TableCell>
              )}
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
}

function Signal({ children, className }: { children: React.ReactNode; className?: string }) {
  return <span className={cn('inline-flex h-5 items-center rounded-full px-2 text-[11px] font-medium whitespace-nowrap', className)}>{children}</span>;
}
